// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, CheckCircle2 } from 'lucide-react'
import { ROUTES }      from '@/lib/constants/routes'
import { useReturnNav } from '@/lib/returnNav'
import { PrimaryActionButton } from '@/components/ui/PrimaryActionButton'

interface BackButtonProps {
  to?:           string
  label?:        string
  variant?:      'top' | 'bottom'
  primaryLabel?: string   // V40-P1: primaire actie label ("Klaar" / "Terug naar dashboard")
  primaryTo?:    string   // V40-P1: primaire actie bestemming (default: DASHBOARD)
}

export function BackButton({
  to    = ROUTES.DASHBOARD,
  label = 'Terug naar vorige scherm',
  variant = 'top',
  primaryLabel,
  primaryTo,
}: BackButtonProps) {
  const navigate = useNavigate()
  // V49-UAT-04: herkomst-bewust. Komt de gebruiker via een interne verwijzing
  // (Documentwijzer, Regiecoach, kennisomgeving), dan wordt de primaire actie
  // contextafhankelijk: "Terug naar [bron]" i.p.v. naar het dashboard.
  const { fromSource, target, label: returnLabel } = useReturnNav()
  const effPrimaryLabel = fromSource ? returnLabel : primaryLabel
  const effPrimaryTo    = fromSource ? target : (primaryTo ?? ROUTES.DASHBOARD)

  function handleBack() {
    if (window.history.length > 2) {
      navigate(-1)
    } else {
      navigate(to)
    }
  }

  function handlePrimary() {
    navigate(effPrimaryTo)
  }

  if (variant === 'bottom') {
    // Primaire knop tonen wanneer er een primaryLabel is óf wanneer de herkomst bekend is
    if (effPrimaryLabel) {
      return (
        <div className="flex flex-col gap-2 mt-2">
          <PrimaryActionButton
            onClick={handlePrimary}
            icon={fromSource ? <ArrowLeft className="h-5 w-5" /> : <CheckCircle2 className="h-5 w-5" />}
          >
            {effPrimaryLabel}
          </PrimaryActionButton>
          <button
            onClick={handleBack}
            className="flex items-center justify-center gap-2 w-full rounded-2xl border-2 border-sand-300
                       bg-white px-5 py-3 text-xs font-medium text-ink-4
                       hover:border-sage-300 hover:text-ink-3
                       transition-all active:scale-[0.98]"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            {label}
          </button>
        </div>
      )
    }

    return (
      <button
        onClick={handleBack}
        className="flex items-center justify-center gap-2 w-full rounded-2xl border-2 border-sand-300
                   bg-white px-5 py-4 text-sm font-medium text-ink-3
                   hover:border-sage-300 hover:text-ink-2
                   transition-all active:scale-[0.98] mt-2"
      >
        <ArrowLeft className="h-4 w-4" />
        {label}
      </button>
    )
  }

  return (
    <button
      onClick={handleBack}
      className="flex items-center gap-1.5 text-sm text-ink-4 hover:text-ink-3 transition-colors mb-5 group"
    >
      <ArrowLeft className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform" />
      {label}
    </button>
  )
}
